/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canAddToCart, getProductState, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { datalayerConstants } from "@rightstore/Utilities/constants";
import { attributeTypeGtmReady, datalayerBeginCheckout } from "@rightstore/Utilities/datalayer";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    HasProductContext;


const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);
    const redirectToCheckoutReviewAndSubmitPage = getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")
    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;
    const selectedVariant = getProductState(state, state.pages.productDetails.selectedProductId);

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        redirectToCheckoutReviewAndSubmitPage,
        quoteProductTypeText: props.productContext.product.properties.quoteProductTypeText,
        isAutoRenewable: selectedVariant.value?.properties?.isAutoRenewable || props.productContext.product.properties.isAutoRenewable
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
    updateCartLine: makeHandlerChainAwaitable<Parameters<typeof updateCartLine>[0], CartLineModel>(updateCartLine)
};

export const productAddToCartButtonStyles: ButtonPresentationProps = {};

const ProductAddToCartButton = ({
    productContext: {
        product: { id: productId, brand, unitListPrice, productTitle, productNumber, attributeTypes },
        productInfo: { qtyOrdered, unitOfMeasure, failedToLoadInventory },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    updateCartLine,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    redirectToCheckoutReviewAndSubmitPage,
    history,
    quoteProductTypeText,
    isAutoRenewable,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(productAddToCartButtonStyles, extendedStyles));

    if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
        return null;
    }

    const addToCartClickHandler = async () => {
        onAddToCartClick?.();

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                    .filter(sectionOptionId => !!sectionOptionId)
                    .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;

        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription
        })) as CartLineModel;

        await updateCartLine({
            cartLine: {
                ...cartline,
                properties: {
                    isAutoRenewable: isAutoRenewable ?? "false",
                    queryParams: window.location.search.replace('?', ''),
                    quoteProductTypeText: quoteProductTypeText ?? ""
                }
            }
        });

        const dataLayerData = {
            /* eslint-disable */
            item_id: productNumber,
            item_name: productTitle,
            item_brand: datalayerConstants.BRAND,
            item_categories: attributeTypeGtmReady(attributeTypes, "Categories"),
            item_customerTypes: attributeTypeGtmReady(attributeTypes, "CustomerType"),
            price: unitListPrice,
            quantity: 1
        };

        datalayerBeginCheckout({ eventName: "begin_checkout", value: unitListPrice.toString(), currency: datalayerConstants.CURRENCY, data: [dataLayerData] });

        redirectToCheckoutReviewAndSubmitPage && history.push(redirectToCheckoutReviewAndSubmitPage.url);
    };

    return (
        <Button
            {...styles}
            onClick={addToCartClickHandler}
            disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled}
            {...otherProps}
        >
            {labelOverride ?? translate("Add to Cart")}
        </Button>
    );
};
export default withHistory(withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton)));
